'use client'

import { captureException } from '@sentry/nextjs'
import { StatsigClient, StatsigOptions, StatsigUser } from '@statsig/js-client'
import { StatsigProvider as BaseStatsigProvider } from '@statsig/react-bindings'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { useAnonymousId } from './anonymousIdContext'
import { useAttribution } from './AttributionContext'
import { useClientGeoLocation } from './clientGeoLocationContext'
import { logger } from 'utils/logger'

const getStatsigSdkKey = (): string =>
  process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY || ''

const getStatsigUser = ({
  anonymousId,
  attribution,
  entryUrlPathname,
  currentUrlPathname,
  state,
}: {
  anonymousId: string | null
  attribution: ReturnType<typeof useAttribution>['attribution']
  entryUrlPathname: string
  currentUrlPathname: string
  state?: string
}): StatsigUser => ({
  userID: anonymousId || '',
  customIDs: { segmentAnonymousID: anonymousId || '' },
  custom: {
    ...attribution,
    entryPathname: entryUrlPathname,
    pathname: currentUrlPathname,
    locationState: state,
  },
})

const getStatsigOptions = (): StatsigOptions => ({
  environment: { tier: process.env.DEPLOYMENT_TARGET },
})

/**
 * @todo: This component could use some improvements, such as:
 * - Currently, this component uses the Statsig JS SDK to create and initialize
 *   the client, but Statsig docs recommend using the Statsig React SDK for this
 *   in React apps.
 * - Currently this component completely reinitializes Statsig when there are
 *   changes to the data which we include in the StatsigUser. Statsig recommends
 *   using the `updateUserAsync` function in such cases (available as a method
 *   on the `StatsigClient`, also returned from the `useStatsigUser` hook).
 * - Currently this component uses custom component state to represent the
 *   initialization state of the Statsig client. A better approach would be to
 *   use the built-in state provided by the client from Statsig's React (or even
 *   JS) SDK for this: `useClientAsyncInit` returns an `isLoading` state which
 *   is true until the Statsig client's initialization is complete but remains
 *   false thereafter even if the `StatsigUser` is updated e.x. via
 *   `updateUserAsync`; `StatsigClient` has a `loadingStatus` property which is
 *   'Uninitialized' prior to initialization, 'Ready' after initialization if
 *   the client isn't updating, and 'Loading' if the client is updating e.x. if
 *   it is processing an update to the `StatsigUser` triggered via
 *   `updateUserAsync`.
 * - Currently this component does not distinguish between the case where it is
 *   not rendered inside of a `ClientGeoLocationProvider`, which would be a
 *   mistake in our code which we we would want to throw an error to help us
 *   catch early, vs the case where location data is unavailable for other
 *   reasons such as the user's privacy settings in which case this component
 *   should continue to simply omit geo location information without throwing an
 *   error.
 *
 * @see https://docs.statsig.com/client/javascript-sdk/init-strategies/#1-asynchronous-initialization---awaited
 * @see https://docs.statsig.com/client/javascript-sdk/next-js/
 * @see https://docs.statsig.com/client/javascript-sdk/react/#updating-user-properties-eg-login
 */
export const StatsigProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const currentUrlPathname = usePathname()
  const anonymousId = useAnonymousId()
  const { attribution } = useAttribution()
  const clientGeoLocationData = useClientGeoLocation()
  const state = clientGeoLocationData.state || undefined

  const [statsigClientState, setStatsigClientState] = useState<
    | {
        isInitializingForTheFirstTime: true
        statsigClient?: never
      }
    | {
        isInitializingForTheFirstTime: false
        statsigClient: StatsigClient
      }
  >({ isInitializingForTheFirstTime: true })

  // Using useState + useEffect here to ensure window access only happens on
  // client side, to avoid errors during next.js server-side pre-rendering
  const [entryUrlPathname, setEntryUrlPathname] = useState<string | null>(null)
  useEffect(
    () => setEntryUrlPathname(new URL(window.location.href).pathname),
    [],
  )

  useEffect(() => {
    let isMounted = true

    if (entryUrlPathname) {
      const initializeStatsig = async () => {
        try {
          const newStatsigClient = new StatsigClient(
            getStatsigSdkKey(),
            getStatsigUser({
              anonymousId,
              attribution,
              entryUrlPathname,
              currentUrlPathname,
              state,
            }),
            getStatsigOptions(),
          )

          await newStatsigClient.initializeAsync()

          // Currently using `isMounted` flag to prevent state updates after
          // unmount because Statsig's SDK does not provide a way to cancel the
          // initialization. If/when Statsig's SDK provides a means of actually
          // cancelling the initialization, we should do that instead.
          if (isMounted) {
            setStatsigClientState({
              isInitializingForTheFirstTime: false,
              statsigClient: newStatsigClient,
            })
          }
        } catch (originalError) {
          const newError = new Error(
            `Error while initializing Statsig client`,
            {
              cause: originalError,
            },
          )
          logger.error(newError)
          captureException(newError)
        }
      }

      initializeStatsig()
    }

    return () => {
      isMounted = false
    }
  }, [anonymousId, attribution, entryUrlPathname, currentUrlPathname, state])

  return statsigClientState.isInitializingForTheFirstTime ? null : (
    <BaseStatsigProvider client={statsigClientState.statsigClient}>
      {children}
    </BaseStatsigProvider>
  )
}
